import { LearnMoreLink } from '@auctisync/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSession } from '../../hooks/useSession';
import { AddCardModal, startAddCard, type AddCardContext } from '../../components/AddCard';
import { invokeEF } from '../../lib/edgeFunctions';
import { friendlyPaymentError } from '../../lib/paymentErrors';
import { supabase } from '../../lib/supabase';

/**
 * /my/payment-methods — the bidder saved-card manager.
 *
 * Cards are stored PER auction house (each tenant is a separate Stripe Connect
 * account, so a card can't be shared across houses). We group by house; the set
 * of houses comes from get_my_payment_method_tenants() (a narrow reader RPC, so
 * bidders never read the tenants table directly).
 */

type TenantRow = {
  tenant_id: string;
  name: string;
  display_name: string | null;
  branding: { logo?: string; primary_color?: string } | null;
};

type Card = {
  id: string;
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
};

// Error copy lives in the shared friendlyPaymentError helper (finding #15) so
// invoice pay and card management surface the same messages.

export function BidderPaymentMethods() {
  const { session, loading: sessionLoading, userId } = useSession();
  const toast = useToast();

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [cardsByTenant, setCardsByTenant] = useState<Record<string, Card[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyPm, setBusyPm] = useState<string | null>(null);
  const [addingTenant, setAddingTenant] = useState<string | null>(null);
  const [addContext, setAddContext] = useState<AddCardContext | null>(null);
  // Idempotency nonce for create-payment-method-setup-intent: minted when an
  // add-card attempt starts, reused if the SAME house's call fails and the
  // bidder retries, cleared once a SetupIntent comes back. Two racing clicks
  // therefore dedupe to one SetupIntent server-side.
  const addNonceRef = useRef<{ tenantId: string; nonce: string } | null>(null);

  const loadCards = useCallback(async (tenantId: string) => {
    const { data, error: err } = await invokeEF<{ cards: Card[] }>('list-payment-methods', {
      tenant_id: tenantId,
    });
    if (err) return; // leave the group empty on a transient list error
    setCardsByTenant((prev) => ({ ...prev, [tenantId]: data?.cards ?? [] }));
  }, []);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase.rpc('get_my_payment_method_tenants');
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      const rows = (data ?? []) as TenantRow[];
      setTenants(rows);
      await Promise.all(rows.map((t) => loadCards(t.tenant_id)));
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, loadCards]);

  const houseName = (t: TenantRow) => t.display_name ?? t.name;

  async function beginAddCard(t: TenantRow) {
    setAddingTenant(t.tenant_id);
    if (!addNonceRef.current || addNonceRef.current.tenantId !== t.tenant_id) {
      addNonceRef.current = { tenantId: t.tenant_id, nonce: crypto.randomUUID() };
    }
    const res = await startAddCard({
      tenantId: t.tenant_id,
      tenantName: houseName(t),
      nonce: addNonceRef.current.nonce,
    });
    setAddingTenant(null);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    // Attempt succeeded — the next "Add a card" click is a fresh attempt.
    addNonceRef.current = null;
    setAddContext(res.context);
  }

  async function setDefault(tenantId: string, pmId: string) {
    setBusyPm(pmId);
    const { error: err } = await invokeEF('set-default-payment-method', {
      tenant_id: tenantId,
      payment_method_id: pmId,
    });
    setBusyPm(null);
    if (err) {
      toast.error(friendlyPaymentError(err));
      return;
    }
    toast.success('Default card updated.');
    await loadCards(tenantId);
  }

  async function removeCard(t: TenantRow, pmId: string) {
    if (!window.confirm(`Remove this card from ${houseName(t)}?`)) return;
    setBusyPm(pmId);
    const { error: err } = await invokeEF('detach-payment-method', {
      tenant_id: t.tenant_id,
      payment_method_id: pmId,
    });
    setBusyPm(null);
    if (err) {
      toast.error(friendlyPaymentError(err));
      return;
    }
    toast.success('Card removed.');
    await loadCards(t.tenant_id);
  }

  if (sessionLoading) return <p className="muted">Loading…</p>;
  if (!session) return <Navigate to="/admin" replace />;

  return (
    <>
      <div className="wa-hd">
        <div>
          <span className="w-lbl">Billing</span>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            Payment methods
          </h1>
          <p className="muted" style={{ fontSize: 14, marginBottom: '0.6rem' }}>
            Save a card with an auction house for one-click checkout on their auctions. Each house
            keeps its own separate, secure record — cards are never shared between businesses.
          </p>
          {/*
          🔴 THE $1 AUTHORISATION IS DISCLOSED HERE, ONCE, WHERE THE CARD IS
          ADDED — and never framed as a per-auction or per-lot hold, because it
          is neither. Saying it at the moment somebody hands over a card is what
          stops them abandoning; saying it later, on a lot page, reads as a
          charge that has just appeared.
        */}
          <p className="muted" style={{ fontSize: 14, marginBottom: '0.6rem' }}>
            A house that requires a card verifies it once, for a dollar, and releases the hold
            straight away. It is not a deposit and it is not per lot — you are charged only for what
            you win, on the invoice after the sale.
          </p>
          <LearnMoreLink helpSlug="saving-a-credit-card" newTab>
            How saved cards work
          </LearnMoreLink>
        </div>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <p className="muted">Loading your cards…</p>
      ) : tenants.length === 0 ? (
        <div className="wa-card" style={{ padding: 20 }}>
          <p className="muted" style={{ margin: 0 }}>
            No auction houses yet. Once you register to bid with a house, you can save a card here
            for faster checkout.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {tenants.map((t) => {
            const cards = cardsByTenant[t.tenant_id] ?? [];
            return (
              <section key={t.tenant_id} className="wa-card" style={{ padding: '18px 20px' }}>
                <header
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.85rem',
                  }}
                >
                  {t.branding?.logo ? (
                    <img
                      src={t.branding.logo}
                      alt=""
                      style={{ height: 28, width: 'auto', borderRadius: 4 }}
                    />
                  ) : null}
                  <h2 className="w-h2" style={{ fontSize: 16, margin: 0, flex: 1 }}>
                    {houseName(t)}
                  </h2>
                  <button
                    type="button"
                    className="w-btn w-btn-out w-btn-sm"
                    onClick={() => {
                      void beginAddCard(t);
                    }}
                    disabled={addingTenant === t.tenant_id}
                  >
                    {addingTenant === t.tenant_id ? 'Opening…' : 'Add a card'}
                  </button>
                </header>

                {cards.length === 0 ? (
                  <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                    No card saved with {houseName(t)} yet.
                  </p>
                ) : (
                  <div className="wa-card">
                    {cards.map((c) => (
                      <div key={c.id} className="wa-row" style={cardRowStyle}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1 }}
                        >
                          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {c.brand}
                          </span>
                          <span className="mono">•••• {c.last4}</span>
                          {c.exp_month && c.exp_year ? (
                            <span className="muted" style={{ fontSize: 13 }}>
                              exp {String(c.exp_month).padStart(2, '0')}/
                              {String(c.exp_year).slice(-2)}
                            </span>
                          ) : null}
                          {c.is_default ? <span className="tag t-nr">Default</span> : null}
                        </div>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          {!c.is_default ? (
                            <button
                              type="button"
                              className="w-btn w-btn-out w-btn-sm"
                              onClick={() => {
                                void setDefault(t.tenant_id, c.id);
                              }}
                              disabled={busyPm === c.id}
                            >
                              Set default
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="w-btn w-btn-out w-btn-sm"
                            onClick={() => {
                              void removeCard(t, c.id);
                            }}
                            disabled={busyPm === c.id}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {addContext ? (
        <AddCardModal
          context={addContext}
          onClose={() => setAddContext(null)}
          onSaved={(pmId) => {
            const tenantId = addContext.tenantId;
            const hadCards = (cardsByTenant[tenantId] ?? []).length > 0;
            setAddContext(null);
            toast.success('Card saved.');
            void (async () => {
              // First card for this house → make it the default so one-click
              // pay preselects it (stripe_customers.default_payment_method
              // otherwise stays NULL). Best-effort: a failure here still
              // leaves a working saved card.
              if (!hadCards && pmId) {
                await invokeEF('set-default-payment-method', {
                  tenant_id: tenantId,
                  payment_method_id: pmId,
                });
              }
              await loadCards(tenantId);
            })();
          }}
        />
      ) : null}
    </>
  );
}

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.875rem',
};

/* Layout only — the fill, the hairline, the radius and the row divider all come
   from `.wa-card` / `.wa-row` now. What is left is the wrap behaviour, which a
   card of saved cards needs and a plain account row does not. */
const cardRowStyle: React.CSSProperties = { flexWrap: 'wrap', fontSize: 14 };
